// ═══════════════════════════════════════════════════════════════════
//  TOQUM · Shared Components & Theme
//  Reused/adapted from Nirvikalp tested components (Sourcing/Dashboard)
// ═══════════════════════════════════════════════════════════════════
import React from 'react';

export const API_URL = 'https://toqum.integrashion.com/api'; // point to your Toqum backend

// ─── THEME (same visual language as Nirvikalp Dashboard) ───────────
export const T = {
  bg:'#f0f2f7', sidebar:'#0a0e1a', card:'#ffffff',
  textMain:'#0a0e1a', textSub:'#8a94a6',
  primary:'#4f6ef7',    // indigo
  success:'#06d6a0',    // teal
  warning:'#ffd166',    // amber
  danger:'#e2445c',     // red
  purple:'#a259f7',
  teal:'#0da99b',
  orange:'#f4845f',
  pink:'#f06292',
  border:'#eef0f6',
  accent:'#06d6a0',
  cardShadow:'0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
};
export const CC = ['#2563eb','#16a34a','#d97706','#dc2626','#7c3aed','#0d9488','#ea580c','#db2777','#0ea5e9','#84cc16'];

// ─── HELPERS ────────────────────────────────────────────────────────
export const uid   = () => Date.now() + Math.floor(Math.random()*1000);
export const today = () => new Date().toISOString().split('T')[0];
export const addDays = (dateStr, days) => {
  const d = new Date(dateStr); d.setDate(d.getDate() + parseInt(days||0,10));
  return d.toISOString().split('T')[0];
};
export const num = v => parseFloat(v)||0;

// RIGHTS: 'approve' | 'edit' | 'entry' | 'view'
export const hasRight = (user, module, right) => {
  if (!user) return false;
  if (user.role === 'Super Admin') return true;
  const r = user.rights?.[module] || [];
  const order = ['view','entry','edit','approve'];
  return order.indexOf(right) <= Math.max(...r.map(x=>order.indexOf(x)), -1);
};
export const isManager = u => u && (u.role==='Manager' || u.role==='Admin' || u.role==='Super Admin');
export const isSuperAdmin = u => u && u.role==='Super Admin';
export const isQAOnly = u => u && u.role === 'QA'; // pure QA — not manager/admin

// Returns supplier names visible to this user (QA sees only allocated ones; managers see all)
export const visibleSuppliers = (user, db) => {
  if (isManager(user)) return (db.suppliers||[]).map(s=>s.name);
  return (db.qaAllocations||[]).filter(a=>a.qaMember===user.name).map(a=>a.supplier);
};

// ─── UI PRIMITIVES ──────────────────────────────────────────────────
export const Page = ({ title, subtitle, children, actions }) => (
  <div style={{ padding:'22px 24px 60px', maxWidth:1400 }}>
    {(title||subtitle||actions) && (
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between',
        flexWrap:'wrap', gap:12, marginBottom:22 }}>
        <div>
          {title && (
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:4, height:22, borderRadius:2, flexShrink:0,
                background:'linear-gradient(180deg,#4f6ef7,#06d6a0)' }}/>
              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:18,
                fontWeight:800, color:'#0a0e1a', letterSpacing:'-0.01em' }}>{title}</div>
            </div>
          )}
          {subtitle && <div style={{ fontSize:11.5, color:'#8a94a6', marginTop:2, marginLeft:14 }}>{subtitle}</div>}
        </div>
        {actions && <div style={{ display:'flex', gap:8 }}>{actions}</div>}
      </div>
    )}
    {children}
  </div>
);

export const Section = ({ title, children, actions }) => (
  <div style={{ background:'white', border:`1px solid ${T.border}`, borderRadius:12,
    marginBottom:16, overflow:'hidden',
    boxShadow:'0 1px 3px rgba(0,0,0,0.04),0 4px 12px rgba(0,0,0,0.03)' }}>
    {title !== undefined && (
      <div style={{ padding:'11px 16px', background:'linear-gradient(90deg,#fafbfd,#f5f7fb)',
        borderBottom:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontWeight:800, fontSize:10.5, textTransform:'uppercase',
          letterSpacing:'0.08em', color:'#3d4660',
          display:'flex', alignItems:'center', gap:7 }}>
          <span style={{ width:6, height:6, borderRadius:'50%', background:T.primary,
            display:'inline-block', flexShrink:0 }}/>
          {title}
        </span>
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>{actions}</div>
      </div>
    )}
    <div style={{ padding:16 }}>{children}</div>
  </div>
);

export const Field = ({ label, value, onChange, type='text', options=null, placeholder='', style={}, disabled=false }) => (
  <div style={{ marginBottom:10, ...style }}>
    <label style={{ fontSize:11, fontWeight:700, display:'block', marginBottom:4, color:T.textSub }}>{label}</label>
    {options ? (
      <select value={value} onChange={onChange} disabled={disabled}
        style={{ width:'100%', padding:'7px 8px', border:`1px solid #cbd5e1`, borderRadius:5, fontSize:12, background: disabled?'#f1f5f9':'white' }}>
        <option value="">— Select —</option>
        {options.map((o,i) => { const v=typeof o==='object'?(o.name||o.value||o.id||String(i)):o; return <option key={v+i} value={v}>{v}</option>; })}
      </select>
    ) : type==='textarea' ? (
      <textarea value={value} onChange={onChange} placeholder={placeholder} rows={3} disabled={disabled}
        style={{ width:'100%', padding:'7px 8px', border:`1px solid #cbd5e1`, borderRadius:5, fontSize:12, resize:'vertical' }} />
    ) : (
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} disabled={disabled}
        style={{ width:'100%', padding:'7px 8px', border:`1px solid #cbd5e1`, borderRadius:5, fontSize:12, background: disabled?'#f1f5f9':'white' }} />
    )}
  </div>
);

export const Grid = ({ cols=3, gap=12, children, mb=0 }) => (
  <div style={{ display:'grid', gridTemplateColumns:`repeat(${cols},1fr)`, gap, marginBottom:mb }}>{children}</div>
);

export const Btn = ({ label, onClick, color=T.primary, small=false, outline=false, disabled=false, icon=null }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ padding: small?'5px 12px':'8px 18px', borderRadius:8,
      fontSize: small?10.5:12, fontWeight:700,
      cursor: disabled?'not-allowed':'pointer', letterSpacing:'0.03em',
      fontFamily:"'Inter',sans-serif",
      border: outline?`1.5px solid ${color}`:'none',
      background: disabled?'#d0d4e4':outline?'white':`linear-gradient(135deg,${color},${color}dd)`,
      color: outline?color:'white',
      display:'inline-flex', alignItems:'center', gap:6,
      boxShadow: disabled||outline?'none':`0 2px 10px ${color}44`,
      transition:'all .15s', opacity: disabled?0.6:1,
      whiteSpace:'nowrap', flexShrink:0 }}>
    {icon}{label}
  </button>
);

export const StatCard = ({ label, value, sub, color=T.primary, onClick }) => (
  <div onClick={onClick} style={{ background:'white', border:`1px solid ${T.border}`,
    borderRadius:12, padding:'16px 18px',
    cursor:onClick?'pointer':'default',
    boxShadow:'0 1px 3px rgba(0,0,0,0.04),0 4px 12px rgba(0,0,0,0.03)',
    transition:'transform .15s,box-shadow .15s',
    position:'relative', overflow:'hidden' }}
    onMouseEnter={e=>{ if(onClick){e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.1)';}}}
    onMouseLeave={e=>{ if(onClick){e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.04),0 4px 12px rgba(0,0,0,0.03)';}}}>
    <div style={{ position:'absolute', bottom:0, left:0, right:0, height:3,
      background:`linear-gradient(90deg,${color},${color}88)` }}/>
    <div style={{ fontSize:9.5, fontWeight:700, textTransform:'uppercase', color:T.textSub,
      letterSpacing:'0.08em', marginBottom:3 }}>{label}</div>
    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:26, fontWeight:800,
      color:'#0a0e1a', lineHeight:1 }}>{value}</div>
    {sub && <div style={{ fontSize:10.5, color:T.textSub, marginTop:4 }}>{sub}</div>}
  </div>
);

export const Tag = ({ label, color=T.primary, onRemove }) => (
  <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:10, fontSize:10.5, fontWeight:700, background:`${color}18`, color, margin:2 }}>
    {label}{onRemove && <span onClick={onRemove} style={{ cursor:'pointer', fontWeight:900 }}>×</span>}
  </span>
);

export const Badge = ({ status }) => {
  const map = {
    'Passed':T.success,'Approved':T.success,'Active':T.success,'Closed':T.success,'Settled':T.success,'Completed':T.success,'Verified':T.success,
    'Pending':T.warning,'In Progress':T.warning,'Scheduled':T.warning,'Open':T.warning,'Offered':T.warning,'Pending Approval':T.warning,'Under Review':T.warning,'Grace Period':T.warning,
    'Failed':T.danger,'Rejected':T.danger,'Blacklisted':T.danger,'Escalated':T.danger,'Overdue':T.danger,'Exception':T.danger,'Blocked':T.danger,
    'Draft':T.textSub,'Not Started':T.textSub,'Arbitration':T.purple,'Split':T.purple,'Debit Note':T.purple,
  };
  const c = map[status] || T.textSub;
  return <span style={{ padding:'2px 9px', borderRadius:10, fontSize:10.5, fontWeight:800, background:`${c}18`, color:c }}>{status}</span>;
};

export const DataTable = ({ columns, rows, renderCell, actions, emptyMsg='No records yet.' }) => (
  <div style={{ overflowX:'auto' }}>
    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
      <thead>
        <tr style={{ background:'#f1f5f9', textAlign:'left' }}>
          {columns.map(c => <th key={c} style={{ padding:'8px 10px', fontSize:10.5, fontWeight:800, textTransform:'uppercase', color:T.textSub, whiteSpace:'nowrap' }}>{c}</th>)}
          {actions && <th style={{ padding:'8px 10px', fontSize:10.5, fontWeight:800, textTransform:'uppercase', color:T.textSub }}>Action</th>}
        </tr>
      </thead>
      <tbody>
        {rows.length===0 && <tr><td colSpan={columns.length+(actions?1:0)} style={{ padding:18, textAlign:'center', color:T.textSub }}>{emptyMsg}</td></tr>}
        {rows.map((r,i) => (
          <tr key={r.id||i} style={{ borderBottom:'1px solid #f1f5f9' }}>
            {columns.map(c => <td key={c} style={{ padding:'8px 10px', verticalAlign:'top' }}>{renderCell(r,c)}</td>)}
            {actions && <td style={{ padding:'8px 10px', whiteSpace:'nowrap' }}>{actions(r)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const RatingStars = ({ value, onChange, max=5, readOnly=false }) => (
  <span>
    {Array.from({length:max},(_,i)=>(
      <span key={i} onClick={()=>!readOnly && onChange && onChange(i+1)}
        style={{ cursor:readOnly?'default':'pointer', fontSize:16, color: i < value ? '#fdab3d' : '#e2e8f0' }}>★</span>
    ))}
  </span>
);

export const TabBar = ({ tabs, active, onChange }) => (
  <div style={{ display:'flex', gap:2, borderBottom:`2px solid ${T.border}`, marginBottom:18,
    flexWrap:'wrap', overflowX:'auto' }}>
    {tabs.map(t => (
      <button key={t} onClick={()=>onChange(t)}
        style={{ padding:'8px 16px', border:'none', cursor:'pointer', fontSize:11, fontWeight:700,
          fontFamily:"'Inter',sans-serif", letterSpacing:'0.05em', textTransform:'uppercase',
          letterSpacing:'0.04em', textTransform:'uppercase', borderRadius:'6px 6px 0 0',
          background: active===t ? `linear-gradient(135deg,${T.primary},#005bbf)` : 'transparent',
          color: active===t ? 'white' : T.textSub,
          boxShadow: active===t ? '0 -2px 8px rgba(0,115,234,0.2)' : 'none',
          transition:'background .15s,color .15s' }}>
        {t}
      </button>
    ))}
  </div>
);

export const FSel = ({ label, value, options, onChange }) => (
  <div>
    <div style={{ fontSize:9, fontWeight:800, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>{label}</div>
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{ padding:'5px 8px', borderRadius:6, border:`1.5px solid ${T.border}`, fontSize:11.5, fontWeight:600, minWidth:110, background:'white' }}>
      <option value="All">All</option>
      {options.map((o,i) => { const v=typeof o==='object'?(o.name||o.value||o.id||String(i)):o; return <option key={v+i} value={v}>{v}</option>; })}
    </select>
  </div>
);

export class ErrorBoundary extends React.Component {
  constructor(p){ super(p); this.state={ hasError:false, msg:'' }; }
  static getDerivedStateFromError(e){ return { hasError:true, msg:e.message }; }
  render(){
    if (this.state.hasError) return (
      <div style={{ padding:40, textAlign:'center' }}>
        <h3>Something went wrong.</h3>
        <p style={{ color:T.textSub }}>{this.state.msg}</p>
        <Btn label="Reload" onClick={()=>window.location.reload()} />
      </div>
    );
    return this.props.children;
  }
}

// ═══════════════════════════════════════════════════════════════════
//  ADDITIONAL PRIMITIVES — ported from Nirvikalp Sourcing.js
// ═══════════════════════════════════════════════════════════════════

export const SCORE_COLORS = { '10':'#16a34a', '5':'#2563eb', '3':'#d97706', '0':'#dc2626', 'NA':'#64748b' };
export const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
export const MONTH_DAYS   = [31,28,31,30,31,30,31,31,30,31,30,31];
export const SUNDAYS      = [4,4,4,5,4,4,4,4,5,4,4,4];

// Editable multi-row table (Sourcing DynamicTable)
export const DynamicTable = ({ columns, data = [], setData, onAdd, addLabel = '+ Add Row', dense = false }) => (
  <div style={{ overflowX:'auto', marginBottom:12 }}>
    <table style={{ width:'100%', borderCollapse:'collapse', fontSize: dense ? 11 : 12 }}>
      <thead>
        <tr style={{ background:'#f1f5f9' }}>
          {columns.map((c,i) => (
            <th key={i} style={{ padding:'8px', textAlign:'left', borderBottom:'1px solid #cbd5e1',
              fontWeight:700, color:'#475569', fontSize:10.5, whiteSpace:'nowrap', minWidth:c.width||'auto' }}>{c.label}</th>
          ))}
          <th style={{ padding:8, borderBottom:'1px solid #cbd5e1', width:40 }}>Del</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row, rIdx) => (
          <tr key={rIdx} style={{ borderBottom:'1px solid #f1f5f9' }}>
            {columns.map((c, cIdx) => (
              <td key={cIdx} style={{ padding:4, verticalAlign:'top' }}>
                {c.type === 'select' ? (
                  <select value={row[c.key]||''} onChange={e=>{ const d=[...data]; d[rIdx]={...d[rIdx],[c.key]:e.target.value}; setData(d); }}
                    style={{ width:'100%', padding:5, border:'1px solid #e2e8f0', borderRadius:3, fontSize:11 }}>
                    <option value="">-</option>
                    {(c.options||[]).map((o,i) => { const v=typeof o==='object'?(o.name||o.value||o.id||String(i)):o; return <option key={v+i} value={v}>{v}</option>; })}
                  </select>
                ) : c.type === 'textarea' ? (
                  <textarea rows={c.rows||2} value={row[c.key]||''} onChange={e=>{ const d=[...data]; d[rIdx]={...d[rIdx],[c.key]:e.target.value}; setData(d); }}
                    style={{ width:'100%', padding:5, border:'1px solid #e2e8f0', borderRadius:3, fontSize:11, boxSizing:'border-box', resize:'vertical' }} />
                ) : (
                  <input type={c.type||'text'} value={row[c.key]||''} onChange={e=>{ const d=[...data]; d[rIdx]={...d[rIdx],[c.key]:e.target.value}; setData(d); }}
                    style={{ width:'100%', padding:5, border:'1px solid #e2e8f0', borderRadius:3, fontSize:11, boxSizing:'border-box' }} />
                )}
              </td>
            ))}
            <td style={{ textAlign:'center' }}>
              <button onClick={()=>setData(data.filter((_,i)=>i!==rIdx))}
                style={{ color:'#ef4444', border:'none', background:'none', cursor:'pointer', padding:4, fontWeight:800 }}>✕</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {onAdd && <button onClick={onAdd}
      style={{ marginTop:6, fontSize:11, color:'#2563eb', background:'none', border:'none', cursor:'pointer', fontWeight:700 }}>
      {addLabel}
    </button>}
  </div>
);

// Google Maps embed by lat,lng (Sourcing MapEmbed)
export const MapEmbed = ({ lat, lng, label = 'Location', height = '180px' }) => {
  if (!lat || !lng) return (
    <div style={{ height, background:'#f1f5f9', borderRadius:8, display:'flex', alignItems:'center',
      justifyContent:'center', fontSize:11, color:T.textSub, border:`1px dashed ${T.border}` }}>
      Enter Lat, Lng to preview {label}
    </div>
  );
  const q = `${lat},${lng}`;
  return (
    <iframe title={label} width="100%" height={height} style={{ border:0, borderRadius:8 }} loading="lazy"
      src={`https://maps.google.com/maps?q=${encodeURIComponent(q)}&z=13&output=embed`} />
  );
};

// Horizontal comparison bar
export const MiniBar = ({ label, value, max, color = T.primary, extra }) => {
  const pct = max > 0 ? Math.min(100, (value/max)*100) : 0;
  return (
    <div style={{ marginBottom:8 }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
        <span style={{ color:T.textMain, fontWeight:600 }}>{label}</span>
        <span style={{ color:T.textSub }}>{extra ?? value}</span>
      </div>
      <div style={{ height:6, background:'#f1f5f9', borderRadius:3, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:3 }} />
      </div>
    </div>
  );
};

// Collapsible section (used by long inspection formats)
export const Collapse = ({ title, children, defaultOpen = false, badge, color = T.primary }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div style={{ border:`1px solid ${T.border}`, borderRadius:8, marginBottom:10, overflow:'hidden', background:'white' }}>
      <div onClick={()=>setOpen(o=>!o)}
        style={{ padding:'10px 14px', background:'#f8fafc', borderBottom: open?`1px solid ${T.border}`:'none',
          cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontWeight:800, fontSize:12, color, letterSpacing:'0.02em' }}>
          {open ? '▾' : '▸'} {title}
        </span>
        {badge && <span style={{ fontSize:10, fontWeight:700, color:T.textSub }}>{badge}</span>}
      </div>
      {open && <div style={{ padding:14 }}>{children}</div>}
    </div>
  );
};

// Modal shell
export const Modal = ({ title, children, onClose, width = 900, actions }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(2px)',
    display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:20 }}>
    <div style={{ background:'white', borderRadius:14, width, maxWidth:'96vw', maxHeight:'92vh',
      display:'flex', flexDirection:'column', overflow:'hidden',
      boxShadow:'0 30px 80px rgba(0,0,0,0.35)' }}>
      <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}`, display:'flex',
        justifyContent:'space-between', alignItems:'center',
        background:'linear-gradient(90deg,#f8fafc,#f1f5f9)' }}>
        <b style={{ fontSize:13.5, color:T.textMain }}>{title}</b>
        <button onClick={onClose} style={{ border:'none', background:'rgba(0,0,0,0.06)',
          fontSize:16, cursor:'pointer', color:T.textSub, width:28, height:28,
          borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
          transition:'background .12s' }}>✕</button>
      </div>
      <div style={{ padding:20, overflow:'auto', flex:1 }}>{children}</div>
      {actions && <div style={{ padding:'13px 20px', borderTop:`1px solid ${T.border}`, display:'flex',
        gap:8, justifyContent:'flex-end', background:'linear-gradient(90deg,#f8fafc,#f1f5f9)' }}>{actions}</div>}
    </div>
  </div>
);

// Yes/No/NA tri-state toggle used across inspection formats
export const TriState = ({ value, onChange, options = ['OK','NG','NA'] }) => {
  const col = { OK:T.success, Yes:T.success, NG:T.danger, No:T.danger, NA:T.textSub, 'N/A':T.textSub };
  return (
    <span style={{ display:'inline-flex', gap:4 }}>
      {options.map(o => (
        <button key={o} type="button" onClick={()=>onChange(value===o ? '' : o)}
          style={{ padding:'2px 9px', fontSize:10, fontWeight:800, borderRadius:4, cursor:'pointer',
            border:`1px solid ${value===o ? (col[o]||T.primary) : T.border}`,
            background: value===o ? (col[o]||T.primary) : 'white',
            color: value===o ? 'white' : T.textSub }}>{o}</button>
      ))}
    </span>
  );
};

// Notification toast
export const Toast = ({ msg, type = 'info', onClose }) => {
  if (!msg) return null;
  const c = { info:T.primary, success:T.success, error:T.danger, warn:T.warning }[type] || T.primary;
  return (
    <div onClick={onClose} style={{ position:'fixed', bottom:24, right:24, zIndex:3000, cursor:'pointer',
      background:'white', borderLeft:`4px solid ${c}`, borderRadius:10, padding:'13px 20px',
      boxShadow:'0 8px 32px rgba(0,0,0,0.18)', fontSize:12.5, fontWeight:600, maxWidth:380,
      animation:'toastIn .2s ease-out' }}>
      <style>{`@keyframes toastIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}`}</style>
      {msg}
    </div>
  );
};

// Print helper — opens a window with A4 styling and triggers print dialog
export const printHTML = (title, bodyHTML) => {
  const w = window.open('', '_blank');
  if (!w) return alert('Please allow pop-ups to print/save PDF.');
  w.document.write(`<html><head><title>${title}</title><style>
    @page { size: A4; margin: 12mm; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10px; color:#111; margin:0; }
    h1 { font-size: 15px; margin: 0 0 2px; } h2 { font-size: 12px; margin: 8px 0 3px; }
    h3 { font-size: 11px; margin: 6px 0 2px; background:#f1f5f9; padding:3px 5px; }
    table { border-collapse: collapse; width: 100%; margin: 4px 0; }
    td, th { border: 1px solid #888; padding: 3px 4px; text-align: left; vertical-align: top; }
    th { background: #eef2f7; font-size: 9px; text-transform: uppercase; }
    .imgs { display:grid; grid-template-columns: repeat(3,1fr); gap:6px; margin-top:6px; }
    .imgs img { width:100%; border:1px solid #bbb; }
    .sign td { height: 42px; }
    .note { font-size:9px; font-style:italic; margin-top:8px; }
    .kv { display:grid; grid-template-columns: repeat(4,1fr); gap:0; }
  </style></head><body>${bodyHTML}
  <script>window.onload=()=>{window.print();}</` + `script></body></html>`);
  w.document.close();
};

// ═══════════════════════════════════════════════════════════════════
//  PAGE LAYOUT + BACKEND HELPERS  (added in the layout rebuild)
// ═══════════════════════════════════════════════════════════════════


// Responsive auto-fitting card row — use instead of fixed Grid cols
export const CardRow = ({ min = 210, gap = 14, children, mb = 16 }) => (
  <div style={{ display:'grid', gridTemplateColumns:`repeat(auto-fill,minmax(${min}px,1fr))`,
    gap, marginBottom:mb }}>{children}</div>
);

// Two-up panel layout that collapses on narrow screens
export const Split = ({ min = 420, gap = 18, children }) => (
  <div style={{ display:'grid', gridTemplateColumns:`repeat(auto-fit,minmax(${min}px,1fr))`, gap }}>
    {children}
  </div>
);

// ── Backend calls ────────────────────────────────────────────────
export const apiPost = async (endpoint, body) => {
  const res = await fetch(`${API_URL}/${endpoint}`, {
    method:'POST', headers:{ 'Content-Type':'application/json' },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  try { return JSON.parse(text.trim().replace(/^\uFEFF/, '')); }
  catch { throw new Error('Server error: ' + text.slice(0,120)); }
};

export const apiGet = async (endpoint, params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([,v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  const res = await fetch(`${API_URL}/${endpoint}${qs ? '?' + qs : ''}`);
  const text = await res.text();
  try { return JSON.parse(text.trim().replace(/^\uFEFF/, '')); }
  catch { throw new Error('Server error: ' + text.slice(0,120)); }
};

// Upload a file to the server; falls back to base64 if the server is down
export const uploadFile = async (file, meta = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  Object.entries(meta).forEach(([k,v]) => v != null && fd.append(k, v));
  try {
    const res = await fetch(`${API_URL}/upload_file.php`, { method:'POST', body: fd });
    const out = await res.json();
    if (out.success) return out;
    throw new Error(out.error || 'upload failed');
  } catch (e) {
    const data = await new Promise(r => {
      const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(file);
    });
    return { success:true, offline:true, url:data, name:file.name,
      size:file.size, isImage:file.type.startsWith('image') };
  }
};

// ── Date range helpers for the dashboard ─────────────────────────
export const RANGE_PRESETS = {
  '7D':   () => [addDays(today(), -7),   today()],
  '1M':   () => [addDays(today(), -30),  today()],
  '3M':   () => [addDays(today(), -90),  today()],
  '6M':   () => [addDays(today(), -180), today()],
  '1Y':   () => [addDays(today(), -365), today()],
  'YTD':  () => [`${new Date().getFullYear()}-01-01`, today()],
  'ALL':  () => ['2000-01-01', today()],
};

export const inRange = (dateStr, from, to) =>
  !!dateStr && dateStr >= from && dateStr <= to;

// Bucket a date into day / week / month for time-series charts
export const bucketOf = (dateStr, granularity) => {
  if (!dateStr) return null;
  if (granularity === 'month') return dateStr.slice(0,7) + '-01';
  if (granularity === 'week') {
    const d = new Date(dateStr);
    const day = (d.getDay() + 6) % 7;          // Monday = 0
    d.setDate(d.getDate() - day);
    return d.toISOString().slice(0,10);
  }
  return dateStr.slice(0,10);
};

// Percentage change between first and last non-null value in a series
export const trendOf = (series, key) => {
  const vals = series.map(s => s[key]).filter(v => v !== null && v !== undefined && !isNaN(v));
  if (vals.length < 2) return null;
  const first = vals[0], last = vals[vals.length-1];
  if (!first) return null;
  return +(((last - first) / Math.abs(first)) * 100).toFixed(1);
};
